/**
 * DashboardView — the presentational "My work" landing (UI/UX Flow 9). Pure props, no
 * hooks: the screen feeds it the `GET /dashboard` response and the permission flags, and
 * the design demo page feeds it sample data. Four regions: status KPIs, "Needs attention"
 * (per-case alerts, most urgent first, each linking to the screen where it is fixed),
 * "My cases" (active cases, most recently updated first) and "Recent activity" (the
 * firm-wide audit feed tagged by case). Every region has loading, empty and error states.
 */
import { Link } from '@tanstack/react-router';
// Tile glyphs are duotone (illustration); the "New case" button stays filled — rule at the
// top of ui/icons.ts. The alert tiles sit in a semantic colour, hence `DUOTONE_TINT`.
import { DUOTONE_TINT, Duotone, Plus } from '../ui/icons.ts';
import type { JSX, ReactNode } from 'react';

import type {
  DashboardActivity,
  DashboardAlert,
  DashboardAlertKind,
  DashboardResponse,
} from '../../lib/api.ts';
import { formatRelative } from '../../lib/format/date.ts';
import { CaseStatusBadge } from '../cases/case-status-badge.tsx';
import { RiskScoreBadge } from '../cases/risk-score-badge.tsx';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Kpi,
  LinkButton,
  PageHeader,
  Skeleton,
  SkeletonText,
  Table,
  Td,
  TextLink,
  Th,
} from '../ui/index.ts';

/* ── Alerts ───────────────────────────────────────────────────────────── */

const ALERT_COPY: Record<
  DashboardAlertKind,
  {
    label: (n: number) => string;
    icon: JSX.Element;
    to: '' | '/statement-analysis' | '/reports';
    tone: 'high' | 'medium' | 'info';
  }
> = {
  unreconciled_statement: {
    label: (n) => (n === 1 ? 'Statement does not reconcile' : `${n} statements do not reconcile`),
    icon: <Duotone.AlertTriangle size={16} aria-hidden="true" {...DUOTONE_TINT} />,
    to: '/statement-analysis',
    tone: 'high',
  },
  manual_review: {
    label: (n) => (n === 1 ? 'Document needs manual review' : `${n} documents need manual review`),
    icon: <Duotone.ScanSearch size={16} aria-hidden="true" {...DUOTONE_TINT} />,
    to: '',
    tone: 'medium',
  },
  failed_report: {
    label: (n) => (n === 1 ? 'Report failed to generate' : `${n} reports failed to generate`),
    icon: <Duotone.FileWarning size={16} aria-hidden="true" {...DUOTONE_TINT} />,
    to: '/reports',
    tone: 'medium',
  },
  self_review_pending: {
    label: (n) =>
      n === 1 ? 'Document awaiting self-review' : `${n} documents awaiting self-review`,
    icon: <Duotone.ShieldCheck size={16} aria-hidden="true" {...DUOTONE_TINT} />,
    to: '',
    tone: 'info',
  },
  open_annotation: {
    label: (n) => (n === 1 ? 'Open comment' : `${n} open comments`),
    icon: <Duotone.MessageSquare size={16} aria-hidden="true" {...DUOTONE_TINT} />,
    to: '',
    tone: 'info',
  },
};

function AlertLink({ alert }: { alert: DashboardAlert }): JSX.Element {
  const copy = ALERT_COPY[alert.kind];
  const params = { caseNumber: alert.case_number };
  const body = (
    <>
      <span className={`argus-alert__icon argus-alert__icon--${copy.tone}`}>{copy.icon}</span>
      <span className="grow">
        <span className="argus-alert__label">{copy.label(alert.count)}</span>
        <span className="argus-alert__case">
          <span className="mono">{alert.case_number}</span> · {alert.client_name}
        </span>
      </span>
    </>
  );
  if (copy.to === '/statement-analysis') {
    return (
      <Link to="/cases/$caseNumber/statement-analysis" params={params} className="argus-alert">
        {body}
      </Link>
    );
  }
  if (copy.to === '/reports') {
    return (
      <Link to="/cases/$caseNumber/reports" params={params} className="argus-alert">
        {body}
      </Link>
    );
  }
  return (
    <Link to="/cases/$caseNumber" params={params} className="argus-alert">
      {body}
    </Link>
  );
}

/* ── Activity ─────────────────────────────────────────────────────────── */

function humaniseAction(action: string): string {
  const spaced = action.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function actorName(user: DashboardActivity['user']): string {
  return user.full_name || user.email || (user.user_id ? 'A colleague' : 'System');
}

export function ActivityFeed({ items }: { items: DashboardActivity[] }): JSX.Element {
  if (items.length === 0) {
    return (
      <EmptyState
        compact
        title="No activity yet"
        description="Actions on your cases will appear here."
      />
    );
  }
  return (
    <ol className="argus-feed">
      {items.map((a, i) => (
        <li key={`${a.timestamp}-${i}`} className="argus-feed__item">
          <span className="argus-feed__dot" aria-hidden="true" />
          <div className="argus-feed__body">
            <span>
              <strong>{humaniseAction(a.action)}</strong>
              <span className="text-muted"> · {actorName(a.user)}</span>
            </span>
            <span className="argus-feed__meta">
              <Link to="/cases/$caseNumber" params={{ caseNumber: a.case_number }} className="mono">
                {a.case_number}
              </Link>
              <span className="text-muted"> · {a.client_name}</span>
              <time dateTime={a.timestamp} className="text-muted">
                {formatRelative(a.timestamp)}
              </time>
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */

export interface DashboardViewProps {
  /** `undefined` while loading. */
  data: DashboardResponse | undefined;
  error?: boolean | undefined;
  /** F-37: the failed dashboard request's correlation id (shown as the support reference). */
  errorRequestId?: string | undefined;
  onRetry?: (() => void) | undefined;
  retrying?: boolean | undefined;
  canCreateCase: boolean;
  /** Administration shortcuts (firm.read / users.read). */
  showFirmSettings?: boolean | undefined;
  showTeam?: boolean | undefined;
  greeting?: ReactNode;
}

export function DashboardView({
  data,
  error = false,
  errorRequestId,
  onRetry,
  retrying = false,
  canCreateCase,
  showFirmSettings = false,
  showTeam = false,
  greeting,
}: DashboardViewProps): JSX.Element {
  const attention = data?.alerts.length ?? 0;
  const kpi = (n: number | undefined): ReactNode =>
    n === undefined ? <Skeleton width={48} height={28} /> : n;

  return (
    <div className="argus-page">
      <PageHeader
        title="Dashboard"
        description={greeting}
        actions={
          canCreateCase ? (
            <LinkButton
              to="/cases/new"
              variant="primary"
              icon={<Plus size={16} aria-hidden="true" />}
            >
              New case
            </LinkButton>
          ) : null
        }
      />

      {error ? (
        <ErrorState
          title="We couldn't load your dashboard"
          message="Try again, or open your cases directly."
          requestId={errorRequestId}
          onRetry={onRetry}
          retrying={retrying}
          actions={<LinkButton to="/cases">Go to cases</LinkButton>}
        />
      ) : null}

      <div className="argus-kpi-grid">
        <Kpi label="Open cases" value={kpi(data?.counts.open)} />
        <Kpi label="In review" value={kpi(data?.counts.in_review)} />
        <Kpi
          label="Needs attention"
          value={
            data ? (
              attention > 0 ? (
                <Badge tone="high">{attention}</Badge>
              ) : (
                <Badge tone="resolved">All clear</Badge>
              )
            ) : (
              <Skeleton width={64} height={28} />
            )
          }
        />
        <Kpi label="Closed" value={kpi(data?.counts.closed)} />
      </div>
      {/* F-04: closed/archived cases leave the default list; give them a way back in. */}
      <TextLink to="/cases" search={{ status: 'closed' }}>
        Closed and archived cases
      </TextLink>

      <div className="argus-dashboard-grid">
        <div className="stack">
          <Card>
            <CardHeader
              title="Needs attention"
              description="Most urgent first. Each item opens where you fix it."
            />
            <CardBody flush>
              {!data ? (
                <div style={{ padding: 'var(--space-4)' }}>
                  <SkeletonText lines={3} label="Loading alerts" />
                </div>
              ) : data.alerts.length === 0 ? (
                <EmptyState
                  compact
                  icon={<Duotone.ShieldCheck size={18} />}
                  title="Nothing needs your attention"
                  description="Statements reconcile, reviews are done and reports rendered."
                />
              ) : (
                <ul className="argus-alerts" aria-label="Needs attention">
                  {data.alerts.map((alert) => (
                    <li key={`${alert.kind}-${alert.case_number}`}>
                      <AlertLink alert={alert} />
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="My cases"
              description="Active cases, most recently updated first."
              actions={<TextLink to="/cases">View all</TextLink>}
            />
            <CardBody flush>
              {!data ? (
                <div style={{ padding: 'var(--space-4)' }}>
                  <SkeletonText lines={4} label="Loading cases" />
                </div>
              ) : data.cases.length === 0 ? (
                <EmptyState
                  compact
                  title="No active cases"
                  description={
                    canCreateCase
                      ? 'Create a case to start an engagement.'
                      : 'You will see cases here once you are added to one.'
                  }
                  action={
                    canCreateCase ? (
                      <LinkButton to="/cases/new" variant="primary" size="sm">
                        New case
                      </LinkButton>
                    ) : undefined
                  }
                />
              ) : (
                <Table caption="My active cases" dense>
                  <thead>
                    <tr>
                      <Th>Case</Th>
                      <Th>Client</Th>
                      <Th>Status</Th>
                      <Th>Risk</Th>
                      <Th>Updated</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cases.map((c) => (
                      <tr key={c.case_number}>
                        <Td mono>
                          <Link to="/cases/$caseNumber" params={{ caseNumber: c.case_number }}>
                            {c.case_number}
                          </Link>
                        </Td>
                        <Td>{c.client_name}</Td>
                        <Td>
                          <CaseStatusBadge status={c.status} />
                        </Td>
                        <Td>
                          <RiskScoreBadge score={c.risk_score} band={c.risk_band} />
                        </Td>
                        <Td>
                          <time dateTime={c.updated_at}>{formatRelative(c.updated_at)}</time>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="stack">
          <Card>
            <CardHeader title="Recent activity" description="Across your cases, newest first." />
            <CardBody>
              {!data ? (
                <SkeletonText lines={6} label="Loading activity" />
              ) : (
                <ActivityFeed items={data.activity} />
              )}
            </CardBody>
          </Card>
          {showFirmSettings ? (
            <Card>
              <CardHeader title="Administration" />
              <CardBody className="stack-sm">
                <Link to="/settings/firm">Firm settings & branding →</Link>
                {showTeam ? <Link to="/settings/team">Team →</Link> : null}
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
